import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { setErrorMessage, setIsLoading, setIsToasterError } from "./app";
import API from "../../api/api";
import { setAdminLoader } from "./admin";
import { ISepaUserData } from "./sepa";

export interface IEigentuemerwechselBody {
  objectId?: string;
  hausnr: string;
  hausnrzusatz?: string;
  strasse: string;
  ort: string;
  // gemeinde: string;
  previousOwnerAnrede: string;
  previousOwnerSurname: string;
  previousOwnerName: string;
  previousOwnerStrasse: string;
  previousOwnerHausnr: string;
  previousOwnerHausnrzusatz?: string;
  previousOwnerPlz: string;
  previousOwnerOrt: string;
  previousOwnerPhoneNumber: string;
  previousOwnerEmail: string;
  newOwnerAnrede: string;
  newOwnerSurname: string;
  newOwnerName: string;
  newOwnerStrasse: string;
  newOwnerHausnr: string;
  newOwnerHausnrzusatz?: string;
  newOwnerPlz: string;
  newOwnerOrt: string;
  newOwnerPhoneNumber: string;
  newOwnerEmail: string;
  newOwnerSince: string;
  newOwnerQuantityOfPeople: string;
  transferDate: string;
  pdfName?: string[];
  ownerType?: "new" | "previous" | undefined;
  quantityOfObjects?: "";
  hausnrto?: string;
  previousOwnerHausnrTo?: string;
  newOwnerHausnrTo?: string;
  tonnenType?: undefined | "available" | "additional" | "remove";
  additionalBioBin?: string;
  additionalPaperBin?: string;
}

export interface IEigentuemerwechselResponseBody extends IEigentuemerwechselBody {
  id: string;
  createdAt: string;
  objectId: string;
  status?: "check" | "accepted" | "declined" | "inWork" | "error";
  kassenzeichen?: string;
}

export interface IAddressData {
  towns: {
    gemeindeNummer: number;
    gemeindeBezeichnung: string;
  }[];
  streets: {
    gemeindeNummer: number;
    ortsteilNummer: number;
    ortsteilBezeichnung: string;
    strasseNummer: number;
    strasseBezeichnung: string;
  }[];
}

interface ISepa {
  admin: {
    data: IEigentuemerwechselResponseBody[];
    itemsCount: number;
    isDataLoading: boolean;
    eigentuemerwechselItem: IEigentuemerwechselResponseBody | null;
  };
  user: {
    userData?: ISepaUserData | null;
    addressData: IAddressData;
  };
}

const initialState: ISepa = {
  admin: {
    data: [],
    itemsCount: 0,
    isDataLoading: false,
    eigentuemerwechselItem: null,
  },
  user: {
    userData: null,
    addressData: {
      streets: [],
      towns: [],
    },
  },
};

export const createEigentuemerwechsel = createAsyncThunk(
  "eigentuemerwechsel/client/createEigentuemerwechsel",
  async (
    body: IEigentuemerwechselBody & {
      acceptHandler: () => void;
      file?: File[];
    },
    { dispatch },
  ) => {
    try {
      const { acceptHandler, file, ...data } = body;
      dispatch(setIsLoading(true));

      await API.eigentuemerwechsel.post.create(data, file);

      acceptHandler();
    } catch (error: any) {
      dispatch(setIsToasterError(true));
      dispatch(setErrorMessage(error?.response?.data?.message));
    } finally {
      dispatch(setIsLoading(false));
    }
  },
);

export const getAdminEigentuemerwechsel = createAsyncThunk(
  "eigentuemerwechsel/admin/getAdminEigentuemerwechsel",
  async (
    {
      direction,
      page,
      pageSize,
      searchValue,
      sort,
      signal,
      endDate,
      startDate,
      status,
    }: {
      sort?: string;
      direction?: string;
      searchValue?: string;
      page?: number;
      pageSize?: number;
      startDate?: Date | null | string;
      endDate?: Date | null | string;
      signal: AbortSignal;
      status?: string[];
    },
    { dispatch },
  ) => {
    try {
      dispatch(setAdminLoader(true));
      const data = API.eigentuemerwechsel.get.getAdminEigentuemerwechselData({
        direction,
        page,
        pageSize,
        searchValue,
        sort,
        signal,
        endDate,
        startDate,
        status,
      });

      return data;
    } catch (error: any) {
      if (error !== "Request canceled") {
        dispatch(setIsToasterError(true));
        dispatch(setErrorMessage(error?.response?.data?.message));
      }
    } finally {
      dispatch(setAdminLoader(false));
    }
  },
);

export const getAdminEigentuemerwechselItemById = createAsyncThunk(
  "eigentuemerwechsel/admin/getAdminEigentuemerwechselItemById",
  async (id: string, { dispatch }) => {
    try {
      dispatch(setAdminLoader(true));
      const data =
        await API.eigentuemerwechsel.get.getAdminEigentuemerwechselItemById(id);

      return data;
    } catch (error: any) {
      dispatch(setIsToasterError(true));
      dispatch(setErrorMessage(error?.response?.data?.message));
    } finally {
      dispatch(setAdminLoader(false));
    }
  },
);

export const updateAdminEigentuemerwechselItemStatusById = createAsyncThunk(
  "eigentuemerwechsel/admin/updateAdminEigentuemerwechselItemStatusById",
  async (
    {
      id,
      status,
      getPdf,
    }: { id: string; status: string; getPdf?: () => Promise<void> },
    { dispatch },
  ) => {
    try {
      dispatch(setAdminLoader(true));
      const data = await API.eigentuemerwechsel.put.updateItemStatusById({
        id,
        status,
      });

      if (getPdf) {
        await getPdf();
      }

      return data;
    } catch (error: any) {
      dispatch(setIsToasterError(true));
      dispatch(setErrorMessage(error?.response?.data?.message));
    } finally {
      dispatch(setAdminLoader(false));
    }
  },
);
export const geEigentuemerwechselFullSummary = createAsyncThunk(
  "eigentuemerwechsel/admin/geEigentuemerwechselFullSummary",
  async ({ id, pdfName }: { id: string; pdfName: string }, { dispatch }) => {
    try {
      dispatch(setAdminLoader(true));
      await API.eigentuemerwechsel.get.geEigentuemerwechselFullSummary(
        id,
        pdfName,
      );
    } catch (error: any) {
      dispatch(setIsToasterError(true));
      dispatch(setErrorMessage(error?.response?.data?.message));
    } finally {
      dispatch(setAdminLoader(false));
    }
  },
);

export const checkUserInfo = createAsyncThunk(
  "eigentuemerwechsel/user/checkUserInfo",
  async ({ objektId }: { objektId: string | number }, { dispatch }) => {
    try {
      dispatch(setIsLoading(true));
      const data = await API.eigentuemerwechsel.get.checkRegulierer({
        objektId,
      });

      return data;
    } catch (error: any) {
      dispatch(setIsToasterError(true));
      dispatch(setErrorMessage(error?.response?.data?.message));
    } finally {
      dispatch(setIsLoading(false));
    }
  },
);
export const getAddressData = createAsyncThunk(
  "eigentuemerwechsel/user/getAddressData",
  async (_, { dispatch }) => {
    try {
      dispatch(setIsLoading(true));
      const data = await API.eigentuemerwechsel.get.getAddressData();

      return data;
    } catch (error: any) {
      dispatch(setIsToasterError(true));
      dispatch(setErrorMessage(error?.response?.data?.message));
    } finally {
      dispatch(setIsLoading(false));
    }
  },
);

const eigentuemerwechselSlice = createSlice({
  name: "eigentuemerwechsel",
  initialState,
  reducers: {
    clearAdminEigentuemerwechselData: (state) => {
      state.admin.data = [];
      state.admin.itemsCount = 0;
    },
    clearAdminEigentuemerwechselItem: (state) => {
      state.admin.isDataLoading = false;
      state.admin.eigentuemerwechselItem = null;
    },
    clearAddressData: (state) => {
      state.user.addressData.streets = [];
      state.user.addressData.towns = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAdminEigentuemerwechsel.pending, (state) => {});
    builder.addCase(getAdminEigentuemerwechsel.fulfilled, (state, action) => {
      if (action.payload) {
        state.admin.itemsCount = action.payload.totalCount;
        state.admin.data = [...action.payload.data];
      }
    });
    builder.addCase(getAdminEigentuemerwechsel.rejected, (state) => {});
    builder.addCase(getAdminEigentuemerwechselItemById.pending, (state) => {
      state.admin.isDataLoading = true;
    });
    builder.addCase(
      getAdminEigentuemerwechselItemById.fulfilled,
      (state, action) => {
        if (action.payload) {
          state.admin.eigentuemerwechselItem = action.payload;
        }
        state.admin.isDataLoading = false;
      },
    );
    builder.addCase(getAdminEigentuemerwechselItemById.rejected, (state) => {
      state.admin.isDataLoading = false;
    });
    builder.addCase(
      updateAdminEigentuemerwechselItemStatusById.pending,
      (state) => {
        state.admin.isDataLoading = true;
      },
    );
    builder.addCase(
      updateAdminEigentuemerwechselItemStatusById.fulfilled,
      (state, action) => {
        if (action.payload) {
          state.admin.eigentuemerwechselItem = action.payload;
        }
        state.admin.isDataLoading = false;
      },
    );
    builder.addCase(
      updateAdminEigentuemerwechselItemStatusById.rejected,
      (state) => {
        state.admin.isDataLoading = false;
      },
    );
    builder.addCase(checkUserInfo.pending, (state) => {});
    builder.addCase(checkUserInfo.fulfilled, (state, action) => {
      if (action.payload) {
        if (action.payload.user) {
          state.user.userData = action.payload.user;
        }
      }
    });
    builder.addCase(checkUserInfo.rejected, (state) => {});
    builder.addCase(getAddressData.pending, (state) => {});
    builder.addCase(getAddressData.fulfilled, (state, action) => {
      if (action.payload) {
        state.user.addressData.streets = action.payload.streets;
        state.user.addressData.towns = action.payload.towns;
      }
    });
    builder.addCase(getAddressData.rejected, (state) => {});
  },
});

export const {
  clearAdminEigentuemerwechselData,
  clearAdminEigentuemerwechselItem,
  clearAddressData,
} = eigentuemerwechselSlice.actions;

export const eigentuemerwechselReducer = eigentuemerwechselSlice.reducer;
